import { paymentsApi } from '$lib/global/payments/api';

const sha256Base64 = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = new Uint8Array(hashBuffer);

    let binary = '';
    for (let i = 0; i < hashArray.byteLength; i++) {
        binary += String.fromCharCode(hashArray[i]);
    }

    return btoa(binary);
};

class Uploader {
    private readonly companyId: string;
    private readonly campaignId: string;
    private readonly downloadUrl: string;

    constructor(companyId: string, campaignId: string) {
        this.companyId = companyId;
        this.campaignId = campaignId;
        this.downloadUrl = import.meta.env.VITE_DOWNLOAD_URL;
    }

    async uploadS3(file: File) {
        const checksum = await sha256Base64(file);

        // 1. Create pre-signed S3 upload URL
        const apiResponse = await paymentsApi.s3.createSignedUrl({
            companyId: this.companyId,
            campaignId: this.campaignId,
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            checksum,
        });

        if (typeof apiResponse === 'string') {
            throw new Error(apiResponse);
        }

        const { uploadId, uploadUrl: presignedUrl } = apiResponse;

        // 2. Upload to pre-signed S3 URL
        const s3response = await fetch(presignedUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': file.type,
                'X-Amz-Sdk-Checksum-Algorithm': 'SHA256',
                'X-Amz-Checksum-Sha256': checksum,
            },
            body: file,
        });

        if (!s3response.ok) {
            throw new Error(`Failed to upload file to S3: ${s3response.statusText}`);
        }

        const queryParams = `filename=${file.name}&filesize=${file.size}`;

        return `${this.downloadUrl}/download/${uploadId}?${encodeURI(queryParams)}`;
    }
}

export default Uploader;
